/**
 * Team → Timesheets pop-ups (prototype tsEdit and waModal):
 *  - Review / file someone's day — PATCH /timesheets/{user_id}/{date}, in the manager's own name. Filing a
 *    `missing` day files it late, "filed by {manager}", and lifts that person's gate.
 *  - Message on WhatsApp — the message is built from what is ticked (their open tasks from GET /tasks and the
 *    missing timesheet), copied or opened in WhatsApp, and recorded as a nudge (POST /nudges).
 */
import { useMemo, useState } from 'react';
import type { Task, TimesheetsResponse, UserRef } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../../api';
import { useSession } from '../../lib/session';
import { firstName, todayIso } from '../../lib/format';
import { useAllTasks } from '../../lib/work';
import { shortDay, useTsWrite } from '../../lib/timesheets';
import { Field, Loading, Modal, Seg, errorText, useToast } from '../../ui/kit';
import { openBlankTab } from '../../lib/tab';
import { copyText } from '../../lib/clipboard';

type Cell = TimesheetsResponse['grid'][number]['cells'][number];

export { copyText };

// ---------------------------------------------------------------- review / file someone's day

export function TsEditModal({ user, date, cell, onClose }: { user: UserRef; date: string; cell: Cell; onClose: () => void }) {
  const miss = cell.status === 'missing';
  const who = firstName(user.name);
  const toast = useToast();
  const [hours, setHours] = useState(miss ? '' : String(cell.hours ?? ''));
  const [planned, setPlanned] = useState('');
  const [done, setDone] = useState('');
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const save = useTsWrite(() =>
    call('timesheets.managerEdit', {
      params: { user_id: user.id, date },
      body: {
        hours_filed: Number(hours),
        ...(planned !== '' ? { planned_minutes: Math.round(Number(planned) * 60) } : {}),
        ...(done !== '' ? { tasks_done: Number(done) } : {}),
        ...(other !== '' ? { other_minutes: Number(other) } : {}),
        note: note.trim(),
      },
    }),
  );
  const submit = () => {
    const e: Record<string, string> = {};
    if (hours === '' || Number.isNaN(Number(hours)) || Number(hours) < 0 || Number(hours) > 24) e['body.hours_filed'] = 'Enter the hours, 0 to 24';
    if (note.trim().length < 3) e['body.note'] = 'Say why you are editing it';
    setErrs(e);
    if (Object.keys(e).length) return;
    save.mutate(undefined, {
      onSuccess: () => {
        toast(`Timesheet saved for ${who}`);
        onClose();
      },
      onError: (err) => {
        if (err instanceof ApiFailure && Object.keys(err.fieldErrors).length) setErrs(err.fieldErrors);
        toast(err instanceof ApiFailure && err.status === 409 ? 'This day is locked and cannot be changed now.' : errorText(err));
      },
    });
  };
  return (
    <Modal
      title={`${miss ? 'File' : 'Review'} ${who}’s timesheet, ${shortDay(date)}`}
      sub={
        miss
          ? `${who} has not filed. Fill it in from what you know, or ask them first. Saving unlocks their next day.`
          : 'Correct anything that looks wrong. The change is marked as edited by you.'
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={save.isPending}>
            Save
          </button>
        </>
      }
    >
      <div className="cols c2">
        <Field label="Hours logged" error={errs['body.hours_filed']}>
          <input className="in" type="number" step="0.25" min="0" max="24" value={hours} onChange={(e) => setHours(e.target.value)} autoFocus />
        </Field>
        <Field label="Hours planned" error={errs['body.planned_minutes']}>
          <input className="in" type="number" step="0.25" min="0" value={planned} onChange={(e) => setPlanned(e.target.value)} />
        </Field>
      </div>
      <div className="cols c2">
        <Field label="Tasks done" error={errs['body.tasks_done']}>
          <input className="in" type="number" min="0" value={done} onChange={(e) => setDone(e.target.value)} />
        </Field>
        <Field label="Other work, minutes" error={errs['body.other_minutes']}>
          <input className="in" type="number" min="0" value={other} onChange={(e) => setOther(e.target.value)} />
        </Field>
      </div>
      <Field label="Why you are changing it" error={errs['body.note']} hint={`${who} and the audit log see this note with your name.`}>
        <textarea className="in" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- WhatsApp

type Tone = 'friendly' | 'firm' | 'thanks';
interface Item {
  g: string;
  id: string;
  line: string;
  on: boolean;
  taskId?: string;
}
const GROUPS = ['Timesheet', 'Overdue', 'Do first today', 'Due today', 'Blocked'];

/**
 * `missingDay`: the person's unfiled day, offered as the Timesheet line. `gated` (default true): the timesheet gate
 * applies to them, so the line adds that their next day stays locked; a non-gated filer (the Ops AD) gets the
 * plain "not filed" line with no lock (t-06836937).
 */
export function WhatsAppModal({ user, missingDay, gated = true, onClose }: { user: UserRef; missingDay: string | null; gated?: boolean; onClose: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const today = todayIso();
  const tasks = useAllTasks({ assignee_id: user.id, status: 'open' });
  const items = useMemo(() => {
    const L: Item[] = [];
    const nm = (t: Task) => (t.client ? `${t.client.name}: ` : '') + t.title;
    if (missingDay)
      L.push({
        g: 'Timesheet',
        id: 'ts',
        line: gated ? `${shortDay(missingDay)}’s timesheet is still not filed, and your next day stays locked until it is` : `${shortDay(missingDay)}’s timesheet is still not filed`,
        on: true,
      });
    const open = (tasks.data ?? []).filter((t) => !t.done);
    const seen = new Set<string>();
    const add = (g: string, t: Task, line: string, on: boolean) => {
      if (seen.has(t.id)) return;
      seen.add(t.id);
      L.push({ g, id: `${g}:${t.id}`, line, on, taskId: t.id });
    };
    open.filter((t) => t.overdue).forEach((t) => add('Overdue', t, `${nm(t)} (${t.days_overdue}d late)`, true));
    open.filter((t) => t.effective_head_priority === 'first').forEach((t) => add('Do first today', t, nm(t), true));
    open.filter((t) => t.due === today).forEach((t) => add('Due today', t, nm(t), false));
    open.filter((t) => t.blocked).forEach((t) => add('Blocked', t, `${nm(t)} (waiting on ${t.blocked!.on})`, false));
    return L;
  }, [tasks.data, missingDay, gated, today]);
  const [on, setOn] = useState<Record<string, boolean> | null>(null);
  const ticked = on ?? Object.fromEntries(items.map((i) => [i.id, i.on]));
  const [tone, setTone] = useState<Tone>(missingDay ? 'firm' : 'friendly');
  const [edited, setEdited] = useState<string | null>(null);
  const f = firstName(user.name);
  const m = firstName(me.user.name);

  const build = () => {
    const sel = items.filter((i) => ticked[i.id]);
    if (!sel.length) return `Hi ${f}, ${m} here. Tick what you want to mention on the left.`;
    const T: Record<Tone, [string, string]> = {
      friendly: [`Hi ${f}, ${m} here. I was going through your tracker (${shortDay(today)}) and wanted to check in on a few things:`, 'Could you update me by 5 pm today? Shout if you need any help. Thanks!'],
      firm: [`Hi ${f}, ${m} here. Going through your tracker today (${shortDay(today)}), these need attention:`, 'Please close each one, or give me a firm date, by 5 pm today.'],
      thanks: [`Hi ${f}, ${m} here. Thank you for the work this week. A quick look at what is still open:`, 'Let’s clear these by Friday. Great job on the rest.'],
    };
    const out = [T[tone][0]];
    for (const g of GROUPS) {
      const l = sel.filter((i) => i.g === g);
      if (l.length) {
        out.push('', `*${g}*`);
        l.forEach((i) => out.push(`• ${i.line}`));
      }
    }
    out.push('', T[tone][1]);
    return out.join('\n');
  };
  const text = edited ?? build();
  const setTick = (next: Record<string, boolean>) => {
    setOn(next);
    setEdited(null);
  };

  // Review t-fdf941aa (1): what gets recorded follows what is ticked. The timesheet line → one timesheet nudge
  // (the server sends "please file {day}"); task lines only → one task nudge per ticked task; nothing ticked
  // → just copy/open, no nudge and no notification.
  const nudgeTargets = (): { kind: 'timesheet' | 'task'; task_id?: string }[] => {
    const sel = items.filter((i) => ticked[i.id]);
    if (sel.some((i) => i.id === 'ts')) return [{ kind: 'timesheet' }];
    return [...new Set(sel.map((i) => i.taskId).filter((t): t is string => !!t))].map((task_id) => ({ kind: 'task', task_id }));
  };
  const nudge = useTsWrite(async (targets: { kind: 'timesheet' | 'task'; task_id?: string }[]) => {
    const out = [];
    for (const t of targets) {
      const body = t.kind === 'task' ? { to_user_id: user.id, kind: 'task' as const, task_id: t.task_id! } : { to_user_id: user.id, kind: 'timesheet' as const };
      out.push(await call('nudges.create', { body, idempotencyKey: newIdempotencyKey() }));
    }
    return out;
  });
  // t-06836937: the nudge is recorded FIRST; the text is copied / WhatsApp opened only once that succeeds, so a
  // failed record never sends a message. The WhatsApp tab is opened blank inside the click (popup blockers allow
  // only that) and pointed at wa.me after the record, or closed if it fails.
  const send = (open: boolean) => {
    const url = `https://wa.me/${(user.phone ?? '').replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
    const win = open ? openBlankTab() : null;
    const deliver = () => {
      if (win) win.location.href = url;
      copyText(text, () => undefined);
      return open && win ? 'Opened in WhatsApp.' : 'Copied. Paste it into WhatsApp.';
    };
    const targets = nudgeTargets();
    if (!targets.length) {
      toast(`${deliver()} Nothing was ticked, so no nudge was recorded.`);
      onClose();
      return;
    }
    nudge.mutate(targets, {
      onSuccess: (ns) => {
        const done = deliver();
        const most = Math.max(...ns.map((n) => n.count));
        toast(`${done} Nudge recorded${most > 1 ? ` (${most} times so far)` : ''}.`);
        onClose();
      },
      onError: (e) => {
        win?.close();
        toast(`${errorText(e)} Nothing was sent.`);
      },
    });
  };
  const groups = [...new Set(items.map((i) => i.g))];

  return (
    <Modal
      wide
      title={`Message ${f} on WhatsApp`}
      sub="Tick what to mention. The message is written for you, and you can edit it before you send."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={() => setTick(Object.fromEntries(items.map((i) => [i.id, true])))}>
            Tick all
          </button>
          <button className="btn" onClick={() => setTick(Object.fromEntries(items.map((i) => [i.id, false])))}>
            Clear
          </button>
          <div className="grow" />
          <button className="btn" onClick={() => send(false)} disabled={nudge.isPending}>
            Copy
          </button>
          <button className="btn pri" onClick={() => send(true)} disabled={nudge.isPending}>
            Open in WhatsApp
          </button>
        </>
      }
    >
      <div className="cols c2" style={{ gap: 18, alignItems: 'start' }}>
        <div style={{ maxHeight: 400, overflow: 'auto', paddingRight: 6 }}>
          {tasks.isLoading ? (
            <Loading />
          ) : groups.length ? (
            groups.map((g) => (
              <div key={g}>
                <div className="lbl" style={{ margin: '10px 0 6px' }}>
                  {g}
                </div>
                {items
                  .filter((i) => i.g === g)
                  .map((i) => (
                    <label key={i.id} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, marginBottom: 7, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!ticked[i.id]} onChange={(e) => setTick({ ...ticked, [i.id]: e.target.checked })} style={{ marginTop: 3 }} />
                      <span>{i.line}</span>
                    </label>
                  ))}
              </div>
            ))
          ) : (
            <div className="sub">Nothing open for this person.</div>
          )}
        </div>
        <div>
          <div style={{ marginBottom: 10 }}>
            <Seg<Tone>
              options={[['friendly', 'Friendly'], ['firm', 'Firm'], ['thanks', 'Thank and nudge']]}
              value={tone}
              onChange={(t) => {
                setTone(t);
                setEdited(null);
              }}
            />
          </div>
          <textarea className="in" rows={13} style={{ fontSize: 13, lineHeight: 1.5 }} value={text} onChange={(e) => setEdited(e.target.value)} aria-label="Message" />
          {!user.phone ? <div className="sub" style={{ fontSize: 12, marginTop: 6 }}>No phone number on file for {f}. Copy the message instead.</div> : null}
        </div>
      </div>
    </Modal>
  );
}
